import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Eye, ShoppingBag, Users, TrendingUp, Clock, Heart, ArrowLeft, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStats = () => {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission('analytics');
  const [tab, setTab] = useState('products');
  const [periodDays, setPeriodDays] = useState(0); // 0 = all time
  const [topProducts, setTopProducts] = useState(null);
  const [clients, setClients] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTopProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats/top-products?days=${periodDays}`);
      setTopProducts(res.data);
    } catch (_) {}
    setLoading(false);
  }, [periodDays]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats/clients`);
      setClients(res.data);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'products') fetchTopProducts();
    if (tab === 'clients') fetchClients();
  }, [tab, fetchTopProducts, fetchClients]);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60));
    if (diff < 1) return "< 1h";
    if (diff < 24) return `${diff}h`;
    const days = Math.floor(diff / 24);
    if (days < 7) return `${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatCurrency = (v) => `${(v || 0).toFixed(2)} €`;

  if (!canAccess) return null;

  const maxSold = topProducts?.top_sold?.[0]?.total_sold || 1;
  const maxViews = topProducts?.top_viewed?.[0]?.view_count || 1;
  const periodLabel = { 0: 'Depuis toujours', 7: '7 derniers jours', 30: '30 derniers jours', 90: '90 derniers jours' }[periodDays];

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-block">
          <ArrowLeft className="w-4 h-4 inline mr-1" />Retour au dashboard
        </Link>
        <h1 className="text-4xl text-white font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Statistiques
        </h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="stats-tabs">
          <button
            type="button"
            onClick={() => setTab('products')}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              tab === 'products' ? 'bg-[color:var(--color-primary)] text-black' : 'border border-white/10 text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-products"
          >
            <BarChart3 className="w-4 h-4" /> Produits
          </button>
          <button
            type="button"
            onClick={() => setTab('clients')}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              tab === 'clients' ? 'bg-[color:var(--color-primary)] text-black' : 'border border-white/10 text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-clients"
          >
            <Users className="w-4 h-4" /> Clients
          </button>
        </div>

        {loading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => <div key={`skel-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-16 animate-pulse" />)}
          </div>
        )}

        {/* Products Tab */}
        {!loading && tab === 'products' && topProducts && (
          <>
            {/* Period filter */}
            <div className="flex flex-wrap items-center gap-2 mb-6 bg-[color:var(--color-surface)] border border-white/10 p-3">
              <Calendar className="w-4 h-4 text-[color:var(--color-primary)] ml-2" />
              <span className="text-zinc-500 text-xs uppercase tracking-wider">Période :</span>
              {[
                { value: 0, label: 'Tout' },
                { value: 7, label: '7j' },
                { value: 30, label: '30j' },
                { value: 90, label: '90j' },
              ].map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriodDays(p.value)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                    periodDays === p.value
                      ? 'bg-[color:var(--color-primary)] text-black'
                      : 'border border-white/10 text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`period-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
              <span className="text-zinc-600 text-xs ml-auto italic">{periodLabel}</span>
            </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Sold */}
            <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
              <h2 className="flex items-center gap-2 text-white text-lg font-light mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <ShoppingBag className="w-5 h-5 text-[color:var(--color-primary)]" /> Produits les plus vendus
              </h2>
              <p className="text-zinc-600 text-xs mb-4">Commandes confirmées → livrées (pas les paniers pending)</p>
              {topProducts.top_sold.length === 0 ? (
                <p className="text-zinc-500 text-sm">Aucune vente enregistrée sur cette période</p>
              ) : (
                <div className="space-y-3" data-testid="top-sold-list">
                  {topProducts.top_sold.map((p, idx) => (
                    <div key={p.product_id} className="flex items-center gap-3">
                      <span className="text-[color:var(--color-primary)] text-sm w-6 text-right font-mono">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-white text-sm truncate">{p.name}</span>
                          <span className="text-zinc-600 text-xs">{p.brand}</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-white/5 overflow-hidden">
                          <div className="h-full bg-[color:rgb(var(--primary-rgb)/0.60)] transition-all" style={{ width: `${(p.total_sold / maxSold) * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-white text-sm">{p.total_sold} vendus</p>
                        <p className="text-zinc-500 text-xs">{formatCurrency(p.total_revenue)}</p>
                        <p className="text-zinc-600 text-[10px]">{p.order_count || 0} cmd</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Viewed */}
            <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
              <h2 className="flex items-center gap-2 text-white text-lg font-light mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <Eye className="w-5 h-5 text-[color:var(--color-primary)]" /> Produits les plus visités
              </h2>
              <p className="text-zinc-600 text-xs mb-4">Toutes les visites (connectées + anonymes)</p>
              {topProducts.top_viewed.length === 0 ? (
                <p className="text-zinc-500 text-sm">Aucune visite enregistrée sur cette période</p>
              ) : (
                <div className="space-y-3" data-testid="top-viewed-list">
                  {topProducts.top_viewed.map((p, idx) => (
                    <div key={p.product_id} className="flex items-center gap-3">
                      <span className="text-[color:var(--color-primary)] text-sm w-6 text-right font-mono">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-white text-sm truncate">{p.name}</span>
                          <span className="text-zinc-600 text-xs">{p.brand}</span>
                        </div>
                        <div className="mt-1 h-1.5 bg-white/5 overflow-hidden">
                          <div className="h-full bg-blue-500/60 transition-all" style={{ width: `${(p.view_count / maxViews) * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-white text-sm">{p.view_count} vues</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {/* Clients Tab */}
        {!loading && tab === 'clients' && clients && (
          <div className="bg-[color:var(--color-surface)] border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex flex-wrap gap-6 text-xs text-zinc-500">
              <span><strong className="text-white">{clients.length}</strong> clients totaux</span>
              <span><strong className="text-white">{clients.filter(c => c.delivered_count > 0).length}</strong> avec commandes livrées</span>
              <span><strong className="text-white">{clients.filter(c => c.status === 'pending').length}</strong> en attente de validation</span>
              <span className="ml-auto italic">Cliquez sur une ligne pour voir les détails</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Client</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Dernière visite</th>
                    <th className="text-center p-4 text-zinc-500 text-xs uppercase tracking-wider">Commandes</th>
                    <th className="text-right p-4 text-zinc-500 text-xs uppercase tracking-wider">Dépensé (livré)</th>
                    <th className="text-right p-4 text-zinc-500 text-xs uppercase tracking-wider">Panier moyen</th>
                    <th className="text-center p-4 text-zinc-500 text-xs uppercase tracking-wider">Vues</th>
                    <th className="text-center p-4 text-zinc-500 text-xs uppercase tracking-wider">Favoris</th>
                  </tr>
                </thead>
                <tbody data-testid="clients-table">
                  {clients.map(client => (
                    <tr key={client.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <p className="text-white text-sm">{client.name}</p>
                          {client.status === 'pending' && (
                            <span className="text-[10px] px-1.5 py-0.5 border border-orange-500/30 bg-orange-500/10 text-orange-400 uppercase tracking-wider">En attente</span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs">{client.phone}</p>
                        {client.email && <p className="text-zinc-600 text-xs">{client.email}</p>}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-zinc-400 text-sm">{formatDate(client.last_visit)}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <p className="text-zinc-300 text-sm">
                          <span className="text-green-500">{client.delivered_count || 0}</span>
                          {client.active_count > 0 && <span className="text-blue-400"> · {client.active_count} en cours</span>}
                          {client.cancelled_count > 0 && <span className="text-red-500/60"> · {client.cancelled_count} annulée(s)</span>}
                        </p>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-[color:var(--color-primary)] text-sm">{formatCurrency(client.total_spent)}</p>
                        {client.potential_spent > client.total_spent && (
                          <p className="text-zinc-600 text-[10px]">+ {formatCurrency(client.potential_spent - client.total_spent)} potentiel</p>
                        )}
                      </td>
                      <td className="p-4 text-right text-zinc-300 text-sm">
                        {client.avg_basket > 0 ? formatCurrency(client.avg_basket) : '—'}
                      </td>
                      <td className="p-4 text-center text-zinc-300 text-sm">
                        <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-zinc-500" />{client.view_count}</span>
                      </td>
                      <td className="p-4 text-center text-zinc-300 text-sm">
                        <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-red-500" />{client.favorites_count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expanded client detail */}
            {expandedClient && (() => {
              const c = clients.find(x => x.id === expandedClient);
              if (!c) return null;
              return (
                <div className="border-t border-white/10 p-4 bg-[color:var(--color-bg)]" data-testid="client-detail">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Inscription</p>
                      <p className="text-zinc-300 text-sm">{formatDate(c.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Dernière commande</p>
                      <p className="text-zinc-300 text-sm">
                        {c.last_order ? `${formatDate(c.last_order.date)} • ${formatCurrency(c.last_order.total)} • ${c.last_order.status}` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Total commandes</p>
                      <p className="text-zinc-300 text-sm">
                        {c.order_count} au total ({c.delivered_count} livrées, {c.active_count} en cours, {c.cancelled_count} annulées)
                      </p>
                    </div>
                  </div>
                  {c.recent_views.length > 0 && (
                    <>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">
                        <Eye className="w-3.5 h-3.5 inline mr-1" />
                        Derniers produits consultés
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {c.recent_views.map((v, i) => (
                          <span key={`${v.name}-${i}`} className="text-xs bg-white/5 border border-white/10 px-2 py-1 text-zinc-400">
                            {v.brand && <span className="text-zinc-600">{v.brand} — </span>}{v.name}
                            <span className="text-zinc-600 ml-1">{formatDate(v.time)}</span>
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {clients.length === 0 && (
              <div className="text-center py-12"><p className="text-zinc-500">Aucun client</p></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStats;
